import { useReducer } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '@components/shared';
import { AgentContact, CurrentUser } from '@root/types';
import api from '@shared/api';
import routes from '@root/routes';
import actions from '@store/actions/listingPage';
import Agent from './Agent';
import InquiryFormModal from './Modal/InquiryFormModal';
import s from './InquiryForm.module.less';

export type Props = {
  listingAddress?: string;
  listingAgents: AgentContact[];
  currentUser?: CurrentUser | null;
  listingId: string;
  hideLocation: boolean;
};

export type InquiryFormModalState = {
  displayAgentContact: boolean;
  selectedAgentIndex: number;
  formError: boolean;
  formSending: boolean;
  formSuccess: boolean;
  visible: boolean;
};

const InquiryFormStateVariants = {
  SENDING: 'sending',
  SUCCESS: 'success',
  FAILURE: 'failure',
};

export type FilledOutFormFields = {
  receiverIds: string[];
  message: string;
  subject: string;
};

export const INITIAL_STATE: InquiryFormModalState = {
  displayAgentContact: false,
  selectedAgentIndex: 0,
  formError: false,
  formSending: false,
  formSuccess: false,
  visible: false,
};

const InquiryForm = ({
  listingAgents,
  currentUser,
  listingId,
  listingAddress,
  hideLocation,
}: Props) => {
  const { t } = useTranslation('listing');
  const analyticsDispatch = useDispatch();
  const { encodedData } = useParams<{ encodedData: string }>();

  const reducer = (state, action) => {
    switch (action.type) {
      case 'RESET':
        return { ...state, ...INITIAL_STATE };

      case InquiryFormStateVariants.SENDING:
        return { ...state, formSending: true };

      case InquiryFormStateVariants.SUCCESS:
        return { ...state, formSending: false, formSuccess: true };

      case InquiryFormStateVariants.FAILURE:
        return { ...state, formSending: false, formError: true };

      case 'TOGGLE_MODAL_VISIBILITY':
        return { ...state, visible: !state.visible };
      case 'REVEAL_AGENT_CONTACT':
        return { ...state, displayAgentContact: true };
      case 'HIDE_AGENT_CONTACT':
        return { ...state, displayAgentContact: false };
      case 'SET_SELECTED_AGENT':
        return { ...state, selectedAgentIndex: action.payload };
      default:
        return state;
    }
  };

  const toggleInquiryVisibility = (): { type: string } => ({ type: 'TOGGLE_MODAL_VISIBILITY' });
  const revealAgentContact = (): { type: string } => ({ type: 'REVEAL_AGENT_CONTACT' });
  const hideAgentContact = (): { type: string } => ({ type: 'HIDE_AGENT_CONTACT' });
  const setSelectedAgentIndex = (agentIndex: number): { type: string; payload: number } => ({
    type: 'SET_SELECTED_AGENT',
    payload: agentIndex,
  });

  const resetModalForm = (): { type: string } => ({
    type: 'RESET',
  });

  const [modalState, dispatch] = useReducer(reducer, {
    ...INITIAL_STATE,
    selectedAgentIndex: 0,
  });

  if (listingAgents.length === 0) return null;

  const handleSendInquiry = async (fields: FilledOutFormFields) => {
    dispatch({ type: InquiryFormStateVariants.SENDING });

    const response = await api.post(routes.api.inquiries(listingId), {
      ...fields,
      fromCustomShare: hideLocation,
      encodedListingShare: encodedData,
    });

    if (response.ok) {
      dispatch({ type: InquiryFormStateVariants.SUCCESS });
      analyticsDispatch(actions.successfulInquiry());
    } else {
      dispatch({ type: InquiryFormStateVariants.FAILURE });
      analyticsDispatch(actions.inquiryError());
    }
  };

  // dismissing the modal without having sent an inquiry
  const handleCloseOnDismiss = () => {
    analyticsDispatch(actions.dismissInquiry());
    dispatch(toggleInquiryVisibility());
  };

  // closing the modal after havng sent a succesful inquiry
  const handleCloseOnSucess = () => {
    dispatch(toggleInquiryVisibility());
    // so the initial state doesn't show until it's re-opened.
    setTimeout(() => {
      dispatch(resetModalForm());
    }, 200);
  };

  const handleOpenModal = () => {
    analyticsDispatch(actions.openInquiryModal());
    dispatch(setSelectedAgentIndex(0));
    dispatch(hideAgentContact());
    dispatch(toggleInquiryVisibility());
  };

  const handleAgentClick = index => {
    dispatch(setSelectedAgentIndex(index));
    dispatch(revealAgentContact());
    dispatch(toggleInquiryVisibility());
  };

  return (
    <>
      <Card data-testid="inquiryCard" title={t('inquireAboutThisListing')}>
        <Button
          onClick={handleOpenModal}
          className={s.expanded}
          icon="email"
          size="large"
          iconClass={s.emailIcon}
          data-testid="mainInquiryButton"
        >
          {t('sendEmail')}
        </Button>
        <ul className={s.listingAgentList}>
          {listingAgents.map((agent, idx) => (
            <Agent key={agent.id} agent={agent} onButtonClick={() => handleAgentClick(idx)} />
          ))}
        </ul>
      </Card>
      <InquiryFormModal
        listingAddress={listingAddress}
        modalState={modalState}
        currentUser={currentUser}
        closeModal={handleCloseOnDismiss}
        onCloseSuccess={handleCloseOnSucess}
        onSendInquiry={handleSendInquiry}
        listingAgents={listingAgents}
      />
    </>
  );
};

export default InquiryForm;
